import React, { useMemo, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import ReactEcharts from 'echarts-for-react'

/**
 * @description Doughnut chart with the distribution of conversations by attention status
 * (attended / not_attended / resolved).
 * @param {{ data: { attention: { attended:number, not_attended:number, resolved:number } } }} props
 */
// Hex literals: ECharts renders to canvas and cannot resolve CSS custom properties.
// attended green matches --secondary-300 (#69B75C) and resolved matches the app primary
// --primary-300 (#00A4E0), both used across the dashboard.
const ATTENTION_COLOR = {
  attended: '#69B75C',
  not_attended: '#EF4444',
  resolved: '#00A4E0'
}

const ATTENTION_KEYS = ['attended', 'not_attended', 'not_required', 'resolved']

function AttentionStatusGraph(props) {
  const { data, height = '100%', onSegmentClick } = props
  const { t, i18n } = useTranslation('clients-dashboard', { keyPrefix: 'reportsScreen' })

  // Ref instead of a dependency so onChartReady only needs to bind once - the click handler
  // always reads the latest onSegmentClick without ever re-registering with the echarts instance.
  const onSegmentClickRef = useRef(onSegmentClick)
  onSegmentClickRef.current = onSegmentClick

  const option = useMemo(() => {
    const attention = data?.attention
    if (!attention) return

    const keys = ATTENTION_KEYS

    return {
      tooltip: { trigger: 'item', confine: true },
      legend: { bottom: 0, icon: 'circle' },
      series: [
        {
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: false,
          itemStyle: { borderColor: '#fff', borderWidth: 2 },
          label: { show: false },
          data: keys.map((key) => ({
            value: attention[key] || 0,
            name: t(`attentionStatus.${key}`),
            itemStyle: { color: ATTENTION_COLOR[key] }
          }))
        }
      ]
    }
  }, [data, i18n.language])

  // echarts-for-react's `onEvents` prop is diffed on every render (see its componentDidUpdate)
  // and any new object/function reference makes it dispose and recreate the whole chart, which
  // can race with a user click and drop it silently. Binding directly to the echarts instance via
  // onChartReady sidesteps that entirely - it fires once when the instance is created and is
  // never re-triggered by unrelated re-renders.
  const handleChartReady = useCallback((instance) => {
    instance.off('click')
    instance.on('click', (params) => {
      if (onSegmentClickRef.current) onSegmentClickRef.current(ATTENTION_KEYS[params.dataIndex])
    })
  }, [])

  return (
    <div className="dashboardView-card dashboard-graph-card">
      <p className="dashboardView-card-title">{t('attentionStatusTitle')}</p>
      {option
        ? <ReactEcharts option={option} style={{ height, cursor: onSegmentClick ? 'pointer' : undefined }} onChartReady={handleChartReady} />
        : <p>N/D</p>}
    </div>
  )
}

export default AttentionStatusGraph
